import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import { EMPTY_FEED, fishReaction } from "@/lib/helpers";
import { Flag } from "lucide-react";

export default function Feed() {
  const { members, catches } = useApp();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m])),
    [members],
  );

  const filtered = useMemo(() => {
    if (filter === "all") return catches;
    return catches.filter((c) => c.catch_owner_id === filter);
  }, [catches, filter]);

  return (
    <div className="pt-4 pb-4">
      <div className="px-5 mb-4">
        <h1 className="font-display text-3xl font-black text-[#0A3D62] mb-1">The Catch Feed</h1>
        <p className="text-sm text-[#556B7D]">Every fish. Every angler. Every bulldust flag.</p>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none px-5 pb-2" data-testid="feed-filters">
        <button
          onClick={() => setFilter("all")}
          data-testid="feed-filter-all"
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-transform active:scale-95 ${
            filter === "all"
              ? "bg-[#0A3D62] text-[#FAF8F3]"
              : "bg-white text-[#0A3D62] border border-[#0A3D62]/15"
          }`}
        >
          All ({catches.length})
        </button>
        {members.map((m) => (
          <button
            key={m.id}
            onClick={() => setFilter(m.id)}
            data-testid={`feed-filter-${m.id}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1.5 transition-transform active:scale-95 ${
              filter === m.id
                ? "bg-[#0A3D62] text-[#FAF8F3]"
                : "bg-white text-[#0A3D62] border border-[#0A3D62]/15"
            }`}
          >
            <span>{m.emoji}</span>
            {m.name}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 px-8" data-testid="empty-feed">
          <div className="text-6xl mb-4">👀</div>
          <p className="text-[#556B7D] text-lg font-semibold">{EMPTY_FEED}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 px-5 mt-2" data-testid="feed-grid">
          {filtered.map((c) => {
            const owner = memberById[c.catch_owner_id];
            const enterer = memberById[c.entered_by_user_id];
            const reaction = fishReaction(c.length_cm);
            const flagged = (c.bulldust_flagged_by || []).length > 0;
            const loggedByOther = c.entered_by_user_id !== c.catch_owner_id;
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/catch/${c.id}`)}
                data-testid={`feed-item-${c.id}`}
                className="text-left bg-white rounded-2xl overflow-hidden border border-[#0A3D62]/5 active:scale-[0.98] transition-transform shadow-sm"
              >
                <div className="aspect-square bg-[#0A3D62]/5 relative">
                  {c.photo_base64 ? (
                    <img
                      src={c.photo_base64}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl">
                      🐟
                    </div>
                  )}
                  {flagged && (
                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#D9381E] flex items-center justify-center shadow-lg">
                      <Flag className="w-4 h-4 text-white" strokeWidth={3} fill="white" />
                    </div>
                  )}
                  <div
                    className="absolute bottom-2 left-2 font-display text-2xl font-black px-2 py-0.5 rounded-lg"
                    style={{
                      background: reaction.color,
                      color: "#FFF",
                    }}
                  >
                    {c.length_cm.toFixed(0)}
                    <span className="text-xs ml-0.5">cm</span>
                  </div>
                </div>
                <div className="p-2.5">
                  {owner && (
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0"
                        style={{ background: owner.color + "33" }}
                      >
                        {owner.emoji}
                      </div>
                      <div className="font-semibold text-sm text-[#0A3D62] truncate flex-1">
                        {owner.name}
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-[#556B7D] mt-1 truncate">
                    {c.species || "Other"}
                    {loggedByOther && enterer && (
                      <span className="italic"> · logged by {enterer.name}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
